import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  FaCalendarAlt,
  FaBook,
  FaUserCircle,
  FaBell,
  FaVideo,
  FaFileDownload,
  FaGoogle,
  FaChalkboardTeacher,
  FaTasks,
  FaCalendarDay,
  FaCalendarPlus,
  FaHistory,
  FaEdit,
  FaTrash,
  FaFileMedical,
  FaCheck,
  FaTimes,
  FaFilePdf,
  FaPaperclip
} from 'react-icons/fa';
import GoogleClassroom from '../../components/GoogleClassroom';
import './StudentDashboard.css';

const StudentDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [classes, setClasses] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [profile, setProfile] = useState(null);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  
  // Leave Management States
  const [leaves, setLeaves] = useState([]);
  const [leaveStats, setLeaveStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    currentMonth: 0
  });
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [editingLeave, setEditingLeave] = useState(null);
  const [leaveForm, setLeaveForm] = useState({
    leaveType: 'medical',
    startDate: '',
    endDate: '',
    reason: '',
    document: null
  });
  const [loadingLeaves, setLoadingLeaves] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedStatus, setSelectedStatus] = useState('all');

  const { user } = useAuth();

  useEffect(() => {
    fetchStudentData();
  }, []);

  useEffect(() => {
    if (activeTab === 'googleClassroom') {
      fetchGoogleCourses();
    }
    if (activeTab === 'leaves') {
      fetchLeaves();
      fetchLeaveStats();
    }
  }, [activeTab]);

  const fetchStudentData = async () => {
    try {
      // Fetch enrolled classes
      setLoadingClasses(true);
      const classesRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/student/classes`);
      setClasses(classesRes.data);
      
      // Fetch notifications
      const notifRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/student/notifications`);
      setNotifications(notifRes.data);
      
      // Fetch profile
      const profileRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/student/profile`);
      setProfile(profileRes.data);
      
      // Fetch assignments
      const assignmentsRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/student/assignments`);
      setAssignments(assignmentsRes.data);
    } catch (error) {
      console.error('Failed to fetch student data:', error);
    } finally {
      setLoadingClasses(false);
    }
  };

  const fetchGoogleCourses = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/google-classroom/student-courses`);
      setEnrolledCourses(response.data.courses || []);
    } catch (error) {
      console.error('Failed to fetch Google Classroom courses:', error);
    }
  };

  // Leave Management Functions
  const fetchLeaves = async () => {
    try {
      setLoadingLeaves(true);
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/leaves/student/${user?.id}`, {
        params: {
          status: selectedStatus !== 'all' ? selectedStatus : undefined,
          year: selectedYear
        }
      });
      setLeaves(response.data.leaves || []);
    } catch (error) {
      console.error('Failed to fetch leaves:', error);
    } finally {
      setLoadingLeaves(false);
    }
  };

  const fetchLeaveStats = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/leaves/student/${user?.id}/stats`);
      setLeaveStats(response.data.stats || {});
    } catch (error) {
      console.error('Failed to fetch leave stats:', error);
    }
  };

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    
    if (!leaveForm.startDate || !leaveForm.endDate || !leaveForm.reason) {
      alert('Please fill all required fields');
      return;
    }

    const startDate = new Date(leaveForm.startDate);
    const endDate = new Date(leaveForm.endDate);
    
    if (startDate > endDate) {
      alert('Start date cannot be after end date');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('studentId', user?.id);
      formData.append('leaveType', leaveForm.leaveType);
      formData.append('startDate', leaveForm.startDate);
      formData.append('endDate', leaveForm.endDate);
      formData.append('reason', leaveForm.reason);
      
      if (leaveForm.document) {
        formData.append('document', leaveForm.document);
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };

      const response = editingLeave
        ? await axios.put(`${process.env.REACT_APP_BACKEND_URL}/leaves/${editingLeave._id}`, formData, config)
        : await axios.post(`${process.env.REACT_APP_BACKEND_URL}/leaves/apply`, formData, config);

      if (response.data.success) {
        alert(response.data.message);
        setShowLeaveModal(false);
        resetLeaveForm();
        fetchLeaves();
        fetchLeaveStats();
      }
    } catch (error) {
      console.error('Leave submission error:', error);
      alert(error.response?.data?.message || 'Failed to submit leave application');
    }
  };

  const handleCancelLeave = async (leaveId) => {
    if (!window.confirm('Are you sure you want to cancel this leave application?')) {
      return;
    }

    try {
      const response = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/leaves/${leaveId}/cancel`);
      if (response.data.success) {
        alert(response.data.message);
        fetchLeaves();
        fetchLeaveStats();
      }
    } catch (error) {
      console.error('Cancel leave error:', error);
      alert(error.response?.data?.message || 'Failed to cancel leave');
    }
  };

  const handleEditLeave = (leave) => {
    setEditingLeave(leave);
    setLeaveForm({
      leaveType: leave.leaveType,
      startDate: new Date(leave.startDate).toISOString().split('T')[0],
      endDate: new Date(leave.endDate).toISOString().split('T')[0],
      reason: leave.reason,
      document: null
    });
    setShowLeaveModal(true);
  };

  const resetLeaveForm = () => {
    setLeaveForm({
      leaveType: 'medical',
      startDate: '',
      endDate: '',
      reason: '',
      document: null
    });
    setEditingLeave(null);
  };

  const handleFileChange = (e) => {
    setLeaveForm({
      ...leaveForm,
      document: e.target.files[0]
    });
  };

  const getStatusBadgeClass = (status) => {
    switch(status) {
      case 'pending': return 'status-pending';
      case 'approved': return 'status-approved';
      case 'rejected': return 'status-rejected';
      case 'cancelled': return 'status-cancelled';
      default: return 'status-pending';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'pending': return <FaHistory />;
      case 'approved': return <FaCheck />;
      case 'rejected': return <FaTimes />;
      case 'cancelled': return <FaTimes />;
      default: return <FaHistory />;
    }
  };

  const getLeaveTypeLabel = (type) => {
    switch(type) {
      case 'medical': return 'Medical Leave';
      case 'personal': return 'Personal Leave';
      case 'emergency': return 'Emergency Leave';
      case 'academic': return 'Academic Leave';
      case 'other': return 'Other';
      default: return type;
    }
  };

  const renderDashboard = () => (
    <div className="student-dashboard">
      {/* Welcome Card */}
      <div className="welcome-card">
        <div className="welcome-info">
          <h2>Welcome, {user?.fullName}!</h2>
          <div className="welcome-details">
            <p><strong>Enrollment ID:</strong> {user?.enrollmentId}</p>
            <p><strong>Program:</strong> {user?.course || 'NTT/MTT'}</p>
            <p><strong>Batch:</strong> {user?.batch || 'NTT 2024'}</p>
            {user?.cohortId && <p><strong>Cohort:</strong> {user?.cohortId}</p>}
          </div>
        </div>
        <div className="profile-avatar">
          <FaUserCircle size={80} />
        </div>
      </div>

      {/* Quick Stats */}
      {/* <div className="quick-stats">
        <div className="stat-card">
          <FaChalkboardTeacher className="stat-icon" />
          <div className="stat-info">
            <h3>{classes.length}</h3>
            <p>Enrolled Classes</p>
          </div>
        </div>
        <div className="stat-card">
          <FaTasks className="stat-icon" />
          <div className="stat-info">
            <h3>{assignments.length}</h3>
            <p>Assignments</p>
          </div>
        </div>
        <div className="stat-card">
          <FaBell className="stat-icon" />
          <div className="stat-info">
            <h3>{notifications.filter(n => !n.read).length}</h3>
            <p>Unread Notifications</p>
          </div>
        </div>
        <div className="stat-card">
          <FaGoogle className="stat-icon" />
          <div className="stat-info">
            <h3>{enrolledCourses.length}</h3>
            <p>Google Classes</p>
          </div>
        </div>
      </div> */}

      {/* Today's Classes */}
      {/* <div className="today-classes">
        <h3><FaCalendarDay /> Today's Schedule</h3>
        {classes.filter(cls => {
          const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
          return cls.schedule?.day === today || cls.day === today;
        }).length > 0 ? (
          <div className="classes-grid">
            {classes.filter(cls => {
              const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
              return cls.schedule?.day === today || cls.day === today;
            }).map(cls => (
              <div key={cls._id || cls.id} className="class-card">
                <div className="class-header">
                  <h4>{cls.title || cls.subject}</h4>
                  <span className={`class-status ${cls.status || 'scheduled'}`}>
                    {cls.status || 'Scheduled'}
                  </span>
                </div>
                <div className="class-details">
                  <p className="class-time">
                    <FaCalendarAlt /> {cls.schedule?.time || cls.time} ({cls.schedule?.duration || '1 hour'})
                  </p>
                  <p className="class-teacher">Teacher: {cls.teacher?.fullName || 'Not assigned'}</p>
                  {cls.room && <p className="class-room">Room: {cls.room}</p>}
                </div>
                <div className="class-actions">
                  {cls.googleMeetLink && (
                    <a href={cls.googleMeetLink} target="_blank" rel="noopener noreferrer" className="btn-join">
                      <FaVideo /> Join Class
                    </a>
                  )}
                  {cls.googleClassroomLink && (
                    <a href={cls.googleClassroomLink} target="_blank" rel="noopener noreferrer" className="btn-classroom">
                      <FaBook /> Classroom
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-classes">
            <p>No classes scheduled for today</p>
            <button className="btn-secondary" onClick={() => setActiveTab('classes')}>
              View All Classes
            </button>
          </div>
        )}
      </div> */}

      {/* Recent Notifications */}
      {/* <div className="notifications-section">
        <div className="section-header">
          <h3><FaBell /> Recent Notifications</h3>
          <button className="btn-text" onClick={() => setActiveTab('notifications')}>
            View All
          </button>
        </div>
        {notifications.length > 0 ? (
          <div className="notifications-list">
            {notifications.slice(0, 5).map(notif => (
              <div key={notif._id} className={`notification-item ${!notif.read ? 'unread' : ''}`}>
                <div className="notification-icon">
                  <FaBell />
                </div>
                <div className="notification-content">
                  <h5>{notif.title}</h5>
                  <p>{notif.message}</p>
                  <span className="notification-time">
                    {new Date(notif.date).toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="no-data">No notifications</p>
        )}
      </div> */}
    </div>
  );

  const renderMyClasses = () => (
    <GoogleClassroom/>
  );

  const renderGoogleClassroom = () => (
    <div className="google-classroom-section">
      <div className="section-header">
        <h2><FaGoogle /> Google Classroom</h2>
        <p className="section-subtitle">
          Access your Google Classroom courses, assignments, and materials
        </p>
      </div>
      
      <div className="google-classroom-container">
        <GoogleClassroom 
          userRole="student"
          showCourses={true}
          showAssignments={true}
          showMaterials={true}
        />
      </div>
    </div>
  );

  const renderLeaves = () => (
    <div className="leaves-section">
      <div className="section-header">
        <h2><FaCalendarPlus /> Leave Applications</h2>
        <button 
          className="btn-primary"
          onClick={() => {
            resetLeaveForm();
            setShowLeaveModal(true);
          }}
        >
          <FaCalendarPlus /> Apply for Leave
        </button>
      </div>

      {/* Leave Statistics */}
      <div className="leave-stats">
        <div className="stat-card">
          <div className="stat-info">
            <h3>{leaveStats.total}</h3>
            <p>Total Leaves</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <h3>{leaveStats.pending}</h3>
            <p>Pending</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <h3>{leaveStats.approved}</h3>
            <p>Approved</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <h3>{leaveStats.rejected}</h3>
            <p>Rejected</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="leave-filters">
        <div className="filter-group">
          <label>Filter by Status:</label>
          <select 
            value={selectedStatus} 
            onChange={(e) => {
              setSelectedStatus(e.target.value);
              fetchLeaves();
            }}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Filter by Year:</label>
          <select 
            value={selectedYear} 
            onChange={(e) => {
              setSelectedYear(e.target.value);
              fetchLeaves();
            }}
          >
            <option value="2024">2024</option>
            <option value="2025">2025</option>
            <option value="2026">2026</option>
            <option value="2027">2027</option>
          </select>
        </div>
      </div>

      {/* Leaves Table */}
      {loadingLeaves ? (
        <div className="loading-container">
          <p>Loading leave applications...</p>
        </div>
      ) : leaves.length === 0 ? (
        <div className="no-leaves">
          <FaCalendarPlus size={60} />
          <h3>No Leave Applications</h3>
          <p>You haven't applied for any leaves yet.</p>
          <button 
            className="btn-primary"
            onClick={() => {
              resetLeaveForm();
              setShowLeaveModal(true);
            }}
          >
            Apply for Your First Leave
          </button>
        </div>
      ) : (
        <div className="leaves-table-container">
          <table className="leaves-table">
            <thead>
              <tr>
                <th>Leave Type</th>
                <th>Period</th>
                <th>Duration</th>
                <th>Status</th>
                <th>Applied On</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {leaves.map(leave => (
                <tr key={leave._id}>
                  <td><span className='hide-for-desktop'>Leave Type: </span>
                    <div className="leave-type">
                      {getLeaveTypeLabel(leave.leaveType)}
                    </div>
                  </td>
                  <td><span className='hide-for-desktop'>Start Date: </span>
                    {new Date(leave.startDate).toLocaleDateString()} - {new Date(leave.endDate).toLocaleDateString()}
                  </td>
                  <td><span className='hide-for-desktop'>Duration: </span>
                    <span className="leave-duration">{leave.totalDays} day(s)</span>
                  </td>
                  <td><span className='hide-for-desktop'>Status: </span>
                    <span className={`status-badge ${getStatusBadgeClass(leave.status)}`}>
                      {getStatusIcon(leave.status)} {leave.status.charAt(0).toUpperCase() + leave.status.slice(1)}
                    </span>
                  </td>
                  <td><span className='hide-for-desktop'>Date: </span>
                    {new Date(leave.createdAt).toLocaleDateString()}
                  </td>
                  <td>
                    <div className="leave-actions">
                      {leave.status === 'pending' && (
                        <>
                          <button 
                            className="btn-edit"
                            onClick={() => handleEditLeave(leave)}
                            title="Edit Leave"
                          >
                            <FaEdit />
                          </button>
                          <button 
                            className="btn-cancel"
                            onClick={() => handleCancelLeave(leave._id)}
                            title="Cancel Leave"
                          >
                            <FaTimes />
                          </button>
                        </>
                      )}
                      {leave.supportingDocument && (
                        <a 
                          href={`${process.env.REACT_APP_BACKEND_URL}/uploads/leaves/${leave.supportingDocument}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-document"
                          title="View Document"
                        >
                          <FaFilePdf />
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Leave Application Modal */}
      {showLeaveModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="modal-header">
              <h2>{editingLeave ? 'Edit Leave Application' : 'Apply for Leave'}</h2>
              <button className="close-btn" onClick={() => {
                setShowLeaveModal(false);
                resetLeaveForm();
              }}>×</button>
            </div>
            
            <form onSubmit={handleLeaveSubmit} className="modal-form">
              <div className="form-group">
                <label>Leave Type </label>
                <select 
                  value={leaveForm.leaveType}
                  onChange={(e) => setLeaveForm({...leaveForm, leaveType: e.target.value})}
                  required
                >
                  <option value="medical">Medical Leave</option>
                  <option value="personal">Personal Leave</option>
                  <option value="emergency">Emergency Leave</option>
                  <option value="academic">Academic Leave</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date </label>
                  <input 
                    type="date"
                    value={leaveForm.startDate}
                    onChange={(e) => setLeaveForm({...leaveForm, startDate: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>End Date </label>
                  <input 
                    type="date"
                    value={leaveForm.endDate}
                    onChange={(e) => setLeaveForm({...leaveForm, endDate: e.target.value})}
                    required
                  />
                </div>
              </div>

              {leaveForm.startDate && leaveForm.endDate && (
                <div className="duration-info">
                  <strong>Total Days:</strong> {
                    Math.ceil((new Date(leaveForm.endDate) - new Date(leaveForm.startDate)) / (1000 * 60 * 60 * 24)) + 1
                  } day(s)
                </div>
              )}

              <div className="form-group">
                <label>Reason for Leave </label>
                <textarea 
                  value={leaveForm.reason}
                  onChange={(e) => setLeaveForm({...leaveForm, reason: e.target.value})}
                  placeholder="Please provide details about your leave..."
                  rows="4"
                  required
                />
              </div>

              {/* <div className="form-group">
                <label>Supporting Document (Optional)</label>
                <div className="file-upload">
                  <input 
                    type="file"
                    onChange={handleFileChange}
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  <div className="file-info">
                    <FaPaperclip /> {leaveForm.document ? leaveForm.document.name : 'No file chosen'}
                  </div>
                  <small>Allowed: PDF, JPG, PNG (Max 5MB)</small>
                </div>
              </div> */}

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => {
                    setShowLeaveModal(false);
                    resetLeaveForm();
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingLeave ? 'Update Application' : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  const renderProfile = () => (
    <div className="student-profile">
      <h2>My Profile</h2>
      {profile ? (
        <div className="profile-details">
          <div className="profile-section">
            <h3>Personal Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Full Name:</label> &nbsp;
                <span>{profile.fullName}</span>
              </div>
              <div className="info-item">
                <label>Father/Husband Name:</label>&nbsp;
                <span>{profile.fatherName || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Date of Birth:</label>&nbsp;
                <span>{profile.dob ? new Date(profile.dob).toLocaleDateString() : 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Email:</label>&nbsp;
                <span>{profile.email}</span>
              </div>
              <div className="info-item">
                <label>Contact:</label>&nbsp;
                <span>{profile.contact}</span>
              </div>
              <div className="info-item">
                <label>Enrollment ID:</label>&nbsp;
                <span className="enrollment-id">{profile.enrollmentId}</span>
              </div>
            </div>
          </div>

          <div className="profile-section">
            <h3>Academic Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Program:</label>&nbsp;
                <span>{profile.course || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Batch:</label>&nbsp;
                <span>{profile.batch || 'N/A'}</span>
              </div>
              {/* <div className="info-item">&nbsp;
                <label>Admission Date:</label>
                <span>{profile.admissionDate ? new Date(profile.admissionDate).toLocaleDateString() : 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Status:</label>&nbsp;
                <span className={`status-badge ${profile.status}`}>{profile.status}</span>
              </div> */}
            </div>
          </div>

          {/* {profile.documents && (
            <div className="profile-section">
              <h3>Documents</h3>
              <div className="documents-grid">
                {profile.documents.aadhar && (
                  <a href={`http://localhost:5000/uploads/${profile.documents.aadhar}`} target="_blank" rel="noopener noreferrer" className="doc-link">
                    <FaFileDownload /> Aadhar Card
                  </a>
                )}
                {profile.documents.tenth && (
                  <a href={`http://localhost:5000/uploads/${profile.documents.tenth}`} target="_blank" rel="noopener noreferrer" className="doc-link">
                    <FaFileDownload /> 10th Certificate
                  </a>
                )}
                {profile.documents.twelfth && (
                  <a href={`http://localhost:5000/uploads/${profile.documents.twelfth}`} target="_blank" rel="noopener noreferrer" className="doc-link">
                    <FaFileDownload /> 12th Certificate
                  </a>
                )}
                {profile.documents.photo && (
                  <a href={`http://localhost:5000/uploads/${profile.documents.photo}`} target="_blank" rel="noopener noreferrer" className="doc-link">
                    <FaFileDownload /> Photo
                  </a>
                )}
                {profile.documents.paymentProof && (
                  <a href={`http://localhost:5000/uploads/${profile.documents.paymentProof}`} target="_blank" rel="noopener noreferrer" className="doc-link">
                    <FaFileDownload /> Payment Receipt
                  </a>
                )}
              </div>
            </div>
          )} */}
        </div>
      ) : (
        <p>Loading profile...</p>
      )}
    </div>
  );

  return (
    <div className="student-dashboard-wrapper">
  <div className="student-container">

    {/* Sidebar */}
    <div className={`student-sidebar ${sidebarOpen ? "open" : ""}`}>

      {/* Close button (mobile only) */}
      <button 
        className="sidebar-close-btn"
        onClick={() => setSidebarOpen(false)}
      >
        ✕
      </button>

      <div className="student-info">
        <FaUserCircle size={50} />
        <h4>{user?.fullName}</h4>
        <p className="student-id">ID: {user?.enrollmentId}</p>
        <p className="student-batch">{user?.batch || user?.course}</p>
      </div>

      <ul className="sidebar-menu">
        <li
          className={activeTab === "dashboard" ? "active" : ""}
          onClick={() => {
            setActiveTab("dashboard");
            setSidebarOpen(false);
          }}
        >
          <FaCalendarAlt /> Dashboard
        </li>

        <li
          className={activeTab === "classes" ? "active" : ""}
          onClick={() => {
            setActiveTab("classes");
            setSidebarOpen(false);
          }}
        >
          <FaBook /> My Classes
        </li>

        <li
          className={activeTab === "leaves" ? "active" : ""}
          onClick={() => {
            setActiveTab("leaves");
            setSidebarOpen(false);
          }}
        >
          <FaCalendarPlus /> Apply for Leave
        </li>

        <li
          className={activeTab === "profile" ? "active" : ""}
          onClick={() => {
            setActiveTab("profile");
            setSidebarOpen(false);
          }}
        >
          <FaUserCircle /> Profile
        </li>
      </ul>
    </div>

    {/* Main Content */}
    <div className="student-content">
      <div className="student-header">

        {/* Mobile Menu Button */}
        <button 
          className="mobile-menu-btn"
          onClick={() => setSidebarOpen(true)}
        >
          ☰
        </button>

        <h1>Student Dashboard</h1>

        <div className="header-actions">
          <span className="welcome-text">
            Welcome back, {user?.fullName?.split(" ")[0] || "Student"}!
          </span>
        </div>

      </div>

      {activeTab === "dashboard" && renderDashboard()}
      {activeTab === "classes" && renderMyClasses()}
      {activeTab === "leaves" && renderLeaves()}
      {activeTab === "profile" && renderProfile()}
    </div>

  </div>
</div>

  );
};

export default StudentDashboard;